import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AtriumApi, { getApiErrorMessage } from "api";
import notify from "lib/notify";
import {
  MdChurch,
  MdCheckCircle,
  MdDelete,
  MdFileDownload,
  MdRadioButtonUnchecked,
  MdVisibility,
} from "react-icons/md";
import { PageContainer, PageHeader } from "components/page";
import Card from "components/card";
import Button, { IconButton } from "components/button";
import Badge from "components/badge";
import Dialog from "components/dialog";
import { useConfirm } from "components/dialog/ConfirmProvider";
import DataTable from "components/data-table";
import { SearchInput, Select } from "components/fields";
import { EmptyState, ErrorState } from "components/states";
import StatCard from "components/stat-card";
import { formatDate, formatDateTime, formatNumber, EMPTY } from "utils/format";

const PAGE_SIZE_OPTIONS = [10, 25, 50];
const DEFAULT_PAGE_SIZE = 25;

const STATUS_OPTIONS = [
  { value: "all", label: "All Requests" },
  { value: "prayed", label: "Prayed For" },
  { value: "not_prayed", label: "Not Yet Prayed" },
];

// The backend `answered` filter maps to the same `isAnswered` column returned as `isPrayed`.
const ANSWERED_PARAM = { all: "", prayed: "&answered=true", not_prayed: "&answered=false" };

const prayersUrl = (limit, offset, filterStatus = "all") =>
  `/connect/prayers?limit=${limit}&offset=${offset}${ANSWERED_PARAM[filterStatus]}`;

// Loads every request for a filter; retries once with the server total if the known total was stale.
const fetchAllPrayers = async (knownTotal, filterStatus) => {
  const response = await AtriumApi.get(prayersUrl(Math.max(knownTotal, 1), 0, filterStatus));
  const { data = [], total = 0 } = response.data;
  if (total <= data.length) return response.data;
  return (await AtriumApi.get(prayersUrl(total, 0, filterStatus))).data;
};

const matchesQuery = (prayer, query) =>
  prayer.name?.toLowerCase().includes(query) ||
  prayer.email?.toLowerCase().includes(query) ||
  prayer.request?.toLowerCase().includes(query);

const displayName = (prayer) => prayer.name || "Anonymous";

const PrayedBadge = ({ isPrayed }) =>
  isPrayed ? <Badge tone="success">Prayed</Badge> : <Badge tone="neutral">Not Yet Prayed</Badge>;

const DetailItem = ({ label, children }) => (
  <div>
    <dt className="text-sm font-medium text-fg-muted">{label}</dt>
    <dd className="mt-1 text-fg">{children}</dd>
  </div>
);

const PrayerDetails = ({ prayer }) => (
  <dl className="flex flex-col gap-4">
    <DetailItem label="Name">
      {prayer.name || EMPTY}
      {prayer.isAnonymous && <span className="ml-2 text-xs text-fg-muted">(Anonymous)</span>}
    </DetailItem>
    {prayer.email && (
      <DetailItem label="Email">
        <a href={`mailto:${prayer.email}`} className="underline underline-offset-2 hover:no-underline">
          {prayer.email}
        </a>
      </DetailItem>
    )}
    <DetailItem label="Prayer Request">
      <p className="whitespace-pre-wrap rounded-control bg-surface-subtle p-3 text-sm">
        {prayer.request}
      </p>
    </DetailItem>
    <DetailItem label="Status">
      <PrayedBadge isPrayed={prayer.isPrayed} />
    </DetailItem>
    <DetailItem label="Submitted">{formatDateTime(prayer.createdAt)}</DetailItem>
    {prayer.isPrayed && prayer.updatedAt && (
      <DetailItem label="Marked as Prayed">{formatDate(prayer.updatedAt)}</DetailItem>
    )}
  </dl>
);

const downloadCsv = (prayers) => {
  const headers = ["Name", "Email", "Prayer Request", "Anonymous", "Prayed For", "Submitted Date"];
  const rows = prayers.map((prayer) => [
    prayer.name || "",
    prayer.email || "",
    prayer.request || "",
    prayer.isAnonymous ? "Yes" : "No",
    prayer.isPrayed ? "Yes" : "No",
    formatDate(prayer.createdAt),
  ]);

  const csvContent = [
    headers.join(","),
    ...rows.map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", `prayer-requests-${Date.now()}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const PrayerRequests = () => {
  const confirm = useConfirm();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedPrayer, setSelectedPrayer] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const requestRef = useRef(0);
  const totalRef = useRef(0);

  // Search runs client-side over every request in the status filter; otherwise the server pages.
  const searching = searchQuery.trim() !== "";
  const fetchLimit = searching ? null : pageSize;
  const fetchOffset = searching ? 0 : pageIndex * pageSize;

  const loadPrayers = useCallback(
    async ({ silent = false } = {}) => {
      const requestId = ++requestRef.current;
      if (!silent) setLoading(true);
      setLoadError(null);
      try {
        const body = fetchLimit
          ? (await AtriumApi.get(prayersUrl(fetchLimit, fetchOffset, filterStatus))).data
          : await fetchAllPrayers(totalRef.current, filterStatus);
        if (requestId !== requestRef.current) return;
        totalRef.current = body.total || 0;
        setRows(body.data || []);
        setTotal(body.total || 0);
      } catch (error) {
        if (requestId === requestRef.current) setLoadError(error);
      } finally {
        if (requestId === requestRef.current) setLoading(false);
      }
    },
    [fetchLimit, fetchOffset, filterStatus]
  );

  // Counts come from the server totals so they cover every request, not just the loaded page.
  const loadStats = useCallback(async () => {
    setStatsError(null);
    try {
      const [all, prayed] = await Promise.all([
        AtriumApi.get(prayersUrl(1, 0)),
        AtriumApi.get(prayersUrl(1, 0, "prayed")),
      ]);
      const totalCount = all.data.total || 0;
      const prayedCount = prayed.data.total || 0;
      setStats({ total: totalCount, prayed: prayedCount, notPrayed: totalCount - prayedCount });
    } catch (error) {
      setStatsError(error);
    }
  }, []);

  useEffect(() => {
    loadPrayers();
  }, [loadPrayers]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const filteredRows = useMemo(() => {
    if (!searching) return rows;
    const query = searchQuery.toLowerCase();
    return rows.filter((prayer) => matchesQuery(prayer, query));
  }, [rows, searching, searchQuery]);

  const pageRows = useMemo(
    () =>
      searching
        ? filteredRows.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize)
        : filteredRows,
    [filteredRows, searching, pageIndex, pageSize]
  );

  const refreshAfterRemoval = useCallback(() => {
    if (pageRows.length === 1 && pageIndex > 0) {
      setPageIndex(pageIndex - 1);
      // Stepping back a server page refetches through the effect.
      if (!searching) return;
    }
    loadPrayers({ silent: true });
  }, [pageRows.length, pageIndex, searching, loadPrayers]);

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setPageIndex(0);
  };

  const handleFilterChange = (value) => {
    setFilterStatus(value);
    setPageIndex(0);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPageIndex(0);
  };

  const clearFilters = () => {
    handleSearchChange("");
    setFilterStatus("all");
  };

  const retryLoad = () => {
    loadStats();
    loadPrayers();
  };

  const closeDetails = () => setSelectedPrayer(null);

  const handleTogglePrayed = useCallback(
    async (prayer) => {
      setTogglingId(prayer.id);
      try {
        const response = await AtriumApi.put(`/connect/prayers/${prayer.id}/answer`, {
          isPrayed: !prayer.isPrayed,
        });
        if (response.data.success) {
          notify.success(prayer.isPrayed ? "Marked as not prayed" : "Marked as prayed for");
          setSelectedPrayer((prev) => (prev?.id === prayer.id ? response.data.data : prev));
          loadStats();
          // Under a status filter the request moves out of the current list.
          if (filterStatus === "all") {
            loadPrayers({ silent: true });
          } else {
            refreshAfterRemoval();
          }
        }
      } catch (error) {
        notify.error(getApiErrorMessage(error, "Could not update prayer request"));
      } finally {
        setTogglingId(null);
      }
    },
    [filterStatus, loadPrayers, loadStats, refreshAfterRemoval]
  );

  const handleDeletePrayer = useCallback(
    async (prayer) => {
      const confirmed = await confirm({
        title: `Delete prayer request from "${displayName(prayer)}"?`,
        message: "This prayer request will be permanently deleted.",
        confirmLabel: "Delete",
        tone: "danger",
      });
      if (!confirmed) return false;

      try {
        const response = await AtriumApi.delete(`/connect/prayers/${prayer.id}`);
        if (!response.data.success) return false;
        notify.success("Prayer request deleted");
        loadStats();
        refreshAfterRemoval();
        return true;
      } catch (error) {
        notify.error(getApiErrorMessage(error, "Could not delete prayer request"));
        return false;
      }
    },
    [confirm, loadStats, refreshAfterRemoval]
  );

  const handleDeleteSelected = async () => {
    if (await handleDeletePrayer(selectedPrayer)) closeDetails();
  };

  const exportToCSV = async () => {
    setExporting(true);
    try {
      const response =
        total > 0 ? await AtriumApi.get(prayersUrl(total, 0, filterStatus)) : null;
      const query = searchQuery.toLowerCase();
      const prayers = (response?.data.data || []).filter(
        (prayer) => !searching || matchesQuery(prayer, query)
      );
      if (prayers.length === 0) {
        notify.error("No data to export");
        return;
      }
      downloadCsv(prayers);
      notify.success("CSV exported");
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not export prayer requests"));
    } finally {
      setExporting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "name",
        header: "Name",
        cell: ({ row }) => (
          <span className="whitespace-nowrap font-medium">
            {row.original.name || EMPTY}
            {row.original.isAnonymous && (
              <span className="ml-1 text-xs font-normal text-fg-muted">(Anonymous)</span>
            )}
          </span>
        ),
      },
      {
        id: "request",
        header: "Request Preview",
        cell: ({ row }) => (
          <p className="max-w-xs truncate text-fg-muted">{row.original.request}</p>
        ),
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) => <PrayedBadge isPrayed={row.original.isPrayed} />,
      },
      {
        id: "submitted",
        header: "Submitted",
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-fg-muted">{formatDate(row.original.createdAt)}</span>
        ),
      },
      {
        id: "actions",
        header: () => <span className="sr-only">Actions</span>,
        meta: { align: "right" },
        cell: ({ row }) => {
          const prayer = row.original;
          const name = displayName(prayer);
          return (
            <div className="flex justify-end gap-1">
              <IconButton
                icon={prayer.isPrayed ? <MdRadioButtonUnchecked /> : <MdCheckCircle />}
                label={
                  prayer.isPrayed
                    ? `Mark request from “${name}” as not prayed`
                    : `Mark request from “${name}” as prayed`
                }
                onClick={() => handleTogglePrayed(prayer)}
                loading={togglingId === prayer.id}
              />
              <IconButton
                icon={<MdVisibility />}
                label={`View request from “${name}”`}
                onClick={() => setSelectedPrayer(prayer)}
              />
              <IconButton
                icon={<MdDelete />}
                label={`Delete request from “${name}”`}
                onClick={() => handleDeletePrayer(prayer)}
              />
            </div>
          );
        },
      },
    ],
    [handleTogglePrayed, handleDeletePrayer, togglingId]
  );

  const error = loadError || statsError;
  const hasPrayers = stats ? stats.total > 0 : true;

  const renderContent = () => {
    if (error) {
      return (
        <Card padding="md">
          <ErrorState
            message={getApiErrorMessage(error, "Could not load prayer requests")}
            onRetry={retryLoad}
          />
        </Card>
      );
    }
    if (!hasPrayers) {
      return (
        <Card padding="md">
          <EmptyState
            icon={MdChurch}
            title="No Prayer Requests Yet"
            description="Prayer request submissions will appear here."
          />
        </Card>
      );
    }
    return (
      <>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          <StatCard
            icon={MdChurch}
            accent="slate"
            label="Total Requests"
            value={formatNumber(stats?.total)}
            loading={!stats}
          />
          <StatCard
            icon={MdCheckCircle}
            label="Prayed For"
            value={formatNumber(stats?.prayed)}
            loading={!stats}
          />
          <StatCard
            icon={MdRadioButtonUnchecked}
            accent="neutral"
            label="Not Yet Prayed"
            value={formatNumber(stats?.notPrayed)}
            loading={!stats}
          />
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <SearchInput
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search by name, email, or request…"
              ariaLabel="Search prayer requests"
              className="w-full md:w-80"
            />
            <Select
              aria-label="Filter by status"
              className="w-full md:w-48"
              value={filterStatus}
              onChange={(e) => handleFilterChange(e.target.value)}
              options={STATUS_OPTIONS}
            />
          </div>
          <DataTable
            aria-label="Prayer requests"
            columns={columns}
            data={pageRows}
            loading={loading}
            getRowId={(prayer) => String(prayer.id)}
            emptyState={
              <EmptyState
                variant="filtered"
                title="No Prayer Requests Match Your Filters"
                action={
                  <Button variant="ghost" onClick={clearFilters}>
                    Clear Filters
                  </Button>
                }
              />
            }
            pagination={{
              pageIndex,
              pageSize,
              total: searching ? filteredRows.length : total,
              onPageChange: setPageIndex,
              onPageSizeChange: handlePageSizeChange,
              pageSizeOptions: PAGE_SIZE_OPTIONS,
            }}
          />
        </div>
      </>
    );
  };

  const selectedToggling = selectedPrayer && togglingId === selectedPrayer.id;

  return (
    <PageContainer>
      <PageHeader
        title="Prayer Requests"
        description="View and manage prayer request submissions"
        actions={
          !error &&
          stats?.total > 0 && (
            <Button
              variant="secondary"
              leftIcon={<MdFileDownload />}
              onClick={exportToCSV}
              loading={exporting}
            >
              Export CSV
            </Button>
          )
        }
      />

      {renderContent()}

      <Dialog
        isOpen={Boolean(selectedPrayer)}
        onClose={closeDetails}
        title="Prayer Request"
        footer={
          <>
            <Button
              variant="secondary"
              leftIcon={<MdDelete />}
              onClick={handleDeleteSelected}
              className="mr-auto"
            >
              Delete
            </Button>
            <Button variant="secondary" onClick={closeDetails}>
              Close
            </Button>
            {selectedPrayer?.isPrayed ? (
              <Button
                variant="secondary"
                leftIcon={<MdRadioButtonUnchecked />}
                onClick={() => handleTogglePrayed(selectedPrayer)}
                loading={selectedToggling}
              >
                Mark as Not Prayed
              </Button>
            ) : (
              <Button
                leftIcon={<MdCheckCircle />}
                onClick={() => handleTogglePrayed(selectedPrayer)}
                loading={selectedToggling}
              >
                Mark as Prayed
              </Button>
            )}
          </>
        }
      >
        {selectedPrayer && <PrayerDetails prayer={selectedPrayer} />}
      </Dialog>
    </PageContainer>
  );
};

export default PrayerRequests;
